import * as React from "react";
import { Link } from "react-router-dom";
import { m as motion, useReducedMotion } from "framer-motion";
import { Building2, CheckCircle2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { TurnstileField } from "@/components/TurnstileField";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, submitVendorSignup } from "@/lib/api";
import { useDeviceFingerprint } from "@/hooks/useDeviceFingerprint";

const industries = [
  "Broadband/Internet",
  "Telephone/VOIP/SIP",
  "Audio/Video",
  "HVAC",
  "Low Voltage",
  "Access Control",
  "Satellite/TV",
  "Security Camera",
  "General Labor",
  "Intercom/Butterfly MX",
  "Automatic Door Opener",
];

const radii = ["200 miles", "100 miles", "50 miles", "25 miles", "10 miles"];

const payRates = [
  "$0-$20",
  "$30-$40",
  "$40-$60",
  "$60-$80",
  "$80-$100",
  "$100-$120",
  "$120-$140",
  "$140-$160",
];

const states = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Illinois",
  "Indiana",
  "Kentucky",
  "Michigan",
  "New York",
  "Ohio",
  "Pennsylvania",
  "Tennessee",
  "Texas",
  "Wisconsin",
];

function message(error: unknown) {
  return error instanceof ApiError || error instanceof Error ? error.message : "Vendor signup failed.";
}

export function VendorSignupPage() {
  const prefersReducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [vendorForm, setVendorForm] = React.useState({
    turnstileToken: "",
    notifyJobs: "",
    selectedState: "",
    selectedPayRate: "",
    selectedIndustries: [] as string[],
    agreed: false,
  });
  const { turnstileToken, notifyJobs, selectedState, selectedPayRate, selectedIndustries, agreed } = vendorForm;
  const setTurnstileToken = (v: string) => setVendorForm((f) => ({ ...f, turnstileToken: v }));
  const setNotifyJobs = (v: string) => setVendorForm((f) => ({ ...f, notifyJobs: v }));
  const setSelectedState = (v: string) => setVendorForm((f) => ({ ...f, selectedState: v }));
  const setSelectedPayRate = (v: string) => setVendorForm((f) => ({ ...f, selectedPayRate: v }));
  const setSelectedIndustries = (v: string[] | ((cur: string[]) => string[])) => setVendorForm((f) => ({ ...f, selectedIndustries: typeof v === "function" ? v(f.selectedIndustries) : v }));
  const setAgreed = (v: boolean) => setVendorForm((f) => ({ ...f, agreed: v }));
  const formRef = React.useRef<HTMLFormElement | null>(null);

  const mutation = useMutation({
    mutationFn: submitVendorSignup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendor-signups"] });
      formRef.current?.reset();
      setNotifyJobs("");
      setSelectedState("");
      setSelectedPayRate("");
      setSelectedIndustries([]);
      setTurnstileToken("");
    },
  });
  const fp = useDeviceFingerprint();

  function toggleIndustry(industry: string) {
    setSelectedIndustries((current) =>
      current.includes(industry) ? current.filter((item) => item !== industry) : [...current, industry],
    );
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.set("cf-turnstile-response", turnstileToken);
    if (fp.fp) formData.set("device_fingerprint", fp.fp);
    formData.delete("job_industry[]");
    selectedIndustries.forEach((industry) => formData.append("job_industry[]", industry));
    mutation.mutate(formData);
  }

  return (
    <main className="min-h-screen px-4 py-8">
      <motion.section
        className="mx-auto max-w-4xl"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.25 }}
      >
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Building2 className="size-6 text-primary" />
              <p className="text-sm font-medium text-primary">Synergy Fiber</p>
            </div>
            <h1 className="text-3xl font-semibold tracking-normal">Vendor Signup</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Submit company details for local job opportunities and Quickbase forwarding.
            </p>
          </div>
          <Link className="text-sm font-medium text-primary" to="/login">Back to sign in</Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Company Information</CardTitle>
            <CardDescription>Fields mirror the original vendor signup form and validation.</CardDescription>
          </CardHeader>
          <CardContent>
            <form ref={formRef} className="grid gap-5" onSubmit={handleSubmit}>
              {mutation.error && (
                <Alert className="border-destructive/20 bg-destructive/10 text-destructive">
                  <div className="mb-1 font-medium leading-none">Signup failed</div>
                  <div className="text-sm">{message(mutation.error)}</div>
                </Alert>
              )}
              {mutation.data && (
                <Alert className="border-success/20 bg-success/10 text-success">
                  <CheckCircle2 className="size-4" />
                  <div className="mb-1 font-medium leading-none">Vendor signup received</div>
                  <div className="text-sm">{mutation.data.vendor_name} was recorded successfully.</div>
                </Alert>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2 md:col-span-2">
                  <Label htmlFor="vendor_name">Company name</Label>
                  <Input id="vendor_name" name="vendor_name" required />
                </div>
                <div className="grid gap-2 md:col-span-2">
                  <Label htmlFor="street_address">Street address</Label>
                  <Textarea id="street_address" name="street_address" required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" name="city" required />
                </div>
                <div className="grid gap-2">
                  <div className="text-sm font-medium leading-none">State</div>
                  <Select value={selectedState} onValueChange={setSelectedState}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                      <SelectContent>
                        {states.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}
                      </SelectContent>
                    </SelectTrigger>
                  </Select>
                  <input type="hidden" name="state" value={selectedState} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="zip">ZIP</Label>
                  <Input id="zip" name="zip" pattern="^[0-9]{5}(-[0-9]{4})?$" required />
                </div>
                <input type="hidden" name="country" value="US" />
                <div className="grid gap-2">
                  <Label htmlFor="vendor_email">Contact email</Label>
                  <Input id="vendor_email" name="vendor_email" type="text" inputMode="email" required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="vendor_phone">Contact phone</Label>
                  <Input id="vendor_phone" name="vendor_phone" type="tel" required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="vendor_sms_phone">SMS phone</Label>
                  <Input id="vendor_sms_phone" name="vendor_sms_phone" type="tel" required />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <div className="text-sm font-medium leading-none">Notify for jobs</div>
                  <Select value={notifyJobs} onValueChange={setNotifyJobs}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose yes or no" />
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </SelectTrigger>
                  </Select>
                  <input type="hidden" name="notify_jobs" value={notifyJobs} />
                </div>
                {notifyJobs === "yes" && (
                  <div className="grid gap-2">
                    <div className="text-sm font-medium leading-none">Notification method</div>
                    <Select value="Email" onValueChange={() => undefined}>
                      <SelectTrigger>
                        <SelectValue />
                        <SelectContent>
                          <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                      </SelectTrigger>
                    </Select>
                    <input type="hidden" name="notify_jobs_method" value="Email" />
                  </div>
                )}
              </div>

              <div className="grid gap-2">
                <div className="text-sm font-medium leading-none">Job industry</div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {industries.map((industry) => (
                    <label key={industry} className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedIndustries.includes(industry)}
                        onChange={() => toggleIndustry(industry)}
                      />
                      {industry}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <div className="text-sm font-medium leading-none">Desired pay rate</div>
                  <Select value={selectedPayRate} onValueChange={setSelectedPayRate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select pay rate" />
                      <SelectContent>
                        {payRates.map((rate) => <SelectItem key={rate} value={rate}>{rate}</SelectItem>)}
                      </SelectContent>
                    </SelectTrigger>
                  </Select>
                  <input type="hidden" name="desired_pay_rate" value={selectedPayRate} />
                </div>
                <div className="grid gap-2">
                  <div className="text-sm font-medium leading-none">Service radius</div>
                  <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" name="service_radius" required defaultValue="">
                    <option value="">Select radius</option>
                    {radii.map((radius) => <option key={radius} value={radius}>{radius}</option>)}
                  </select>
                </div>
              </div>

              <TurnstileField value={turnstileToken} onChange={setTurnstileToken} />

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  required
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms-of-service" className="text-primary underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="text-primary underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>,
                  including the collection and processing of my personal data, GPS location, and photographs for identity verification and job-matching purposes.
                </span>
              </label>

              <Button type="submit" disabled={mutation.isPending || !turnstileToken || selectedIndustries.length === 0 || !notifyJobs || !selectedState || !selectedPayRate || !agreed}>
                {mutation.isPending ? "Submitting..." : "Submit Vendor Signup"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.section>
    </main>
  );
}
